<template>
  <b-card
    class="rounded border-0 shadow-sm flex-fill"
    :class="bgClass"
    body-class="p-0 h-100 d-flex flex-column justify-content-center align-items-center"
    :data-me="`media-panel-wrapper-${alert.rawAlertId}`"
  >
    <!-- State: Loading -->
    <template v-if="isLoading">
      <div class="d-flex mb-2" :data-me="`media-panel-loading-state-${alert.rawAlertId}`">
        <span class="dot mx-1"></span>
        <span class="dot mx-1"></span>
        <span class="dot mx-1"></span>
      </div>
      <span class="text-white fs-6 fw-bold text-center">{{ loadingMessage }}...</span>
    </template>

    <!-- State: Failed -->
    <template v-else-if="isFailed">
      <i class="mc-icon icon-fill_video-off fs-4 text-white"></i>
      <span class="text-white fs-6 fw-bold text-center px-3 py-2">{{ $t('global.noMedia') }}</span>
    </template>
    <!-- State: Check Media -->
    <template v-else-if="alert.canCheck">
      <span class="text-white fs-6 fw-bold text-center px-3 py-2">{{
        $t('notification.text.mediaNotAvailable')
      }}</span>
      <McButton
        :data-me="`media-panel-btn-check-media-${alert.rawAlertId}`"
        variant="primary"
        :loading="isChecking"
        :text="$t('global.action.checkAgain')"
        size="sm"
        @click="emitOnce('retry-checking')"
      />
    </template>
    <!-- State : Request Media -->
    <template v-else-if="alert.canRequest && !alert.isOfflineDevice">
      <i class="mc-icon icon-outline_download fs-4 text-white"></i>
      <span class="text-white fs-6 fw-bold text-center px-3 py-2">{{ $t('global.noMedia') }}</span>
      <McButton
        :data-me="`media-panel-btn-request-media-${alert.rawAlertId}`"
        variant="primary"
        :loading="isRequesting"
        text="Request Media"
        size="sm"
        @click="emitOnce('request-media')"
      />
    </template>
    <!-- State: Offline Device -->
    <template v-else-if="alert.isOfflineDevice">
      <i class="mc-icon icon-fill_wifi-off fs-4 text-white"></i>
      <span class="text-white fs-6 fw-bold text-center px-3 py-2">{{
        $t('global.action.cannotReachDevice')
      }}</span>
      <McButton
        :data-me="`media-panel-btn-offline-device-${alert.rawAlertId}`"
        variant="primary"
        :loading="isRequesting"
        :text="$t('global.action.tryAgain')"
        size="sm"
        @click="emitOnce('request-media')"
      />
    </template>
    <!-- State: Media -->
    <template v-else-if="hasMedia">
      <div class="w-100 h-100 position-relative media-wrapper">
        <template v-if="isVideoMedia">
          <VideoPlayerMux
            v-if="isTsFormat"
            :key="videoSrc"
            :src="videoSrc"
            :player-id="`mux-player-${alert.rawAlertId}`"
            :controls="false"
            class="w-100 h-100 vjs-custom-skin video-js rounded"
            @ready="onMuxReady"
            @error="onMuxError"
          />
          <VideoPlayer
            v-else
            :options="videoPlayerOptions"
            :events="['error', 'loadeddata']"
            :playsinline="true"
            class="w-100 h-100 vjs-custom-skin"
            @error="onMediaError"
          />
          <div
            v-if="!muxFailed"
            class="play-overlay"
            :data-me="`media-panel-play-overlay-${alert.rawAlertId}`"
            @click="emitOnce('play-media')"
          >
            <i
              v-if="alert.videoData?.identifier === 'V'"
              class="mc-icon icon-fill_play fs-1 text-white"
            ></i>
          </div>
          <div
            v-else
            class="w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-dark-blue"
          >
            <i class="mc-icon icon-outline_video fs-2 text-white"></i>
            <span class="text-white fs-6 fw-bold text-center px-3 py-2">{{
              $t('global.noMedia')
            }}</span>
          </div>
        </template>
        <img
          v-else
          :data-me="`media-panel-img-${alert.rawAlertId}`"
          :src="imageSrc"
          class="w-100 h-100"
          style="object-fit: cover; border-radius: 4px; cursor: pointer"
          @error="onMediaError"
          @click="emitOnce('play-media')"
        />
      </div>
    </template>
  </b-card>
</template>

<script>
import McButton from '@/components/McButton'
import VideoPlayer from '@/components/Video/VideoPlayer.vue'
import VideoPlayerMux from '@/components/Video/VideoPlayerMux.vue'
import {
  hasMediaPanelContent,
  isMediaPanelFailed,
  isMediaPanelLoading,
  CONNECTING_PROCESS,
  UPLOADING_PROCCESS,
} from '../lib/alert-media'

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'avi', 'mkv', 'ts'])
const MUX_REQUIRED_EXTENSIONS = new Set(['ts'])

export default {
  name: 'MediaPanel',

  components: { McButton, VideoPlayer, VideoPlayerMux },

  props: {
    alert: {
      type: Object,
      required: true,
    },
  },

  emits: ['retry-checking', 'play-media', 'request-media', 'refresh-media'],

  data() {
    return {
      pendingEvents: {},
      muxFailed: false,
      isRefreshing: false,
      refreshedMediaUrl: null,
    }
  },

  computed: {
    pollingStatus() {
      return this.alert.pollingStatus
    },

    isFailed() {
      return isMediaPanelFailed(this.alert)
    },

    isChecking() {
      return this.pollingStatus === 'checking'
    },
    isRequesting() {
      return this.pollingStatus === 'requesting'
    },
    hasNoConnectivity() {
      return !this.alert.canCheck && !this.alert.canRequest
    },

    isLoading() {
      return this.isRefreshing || isMediaPanelLoading(this.alert)
    },

    loadingMessage() {
      if (CONNECTING_PROCESS.includes(this.pollingStatus)) return this.$t('trackVideo.connecting')
      if (UPLOADING_PROCCESS.includes(this.pollingStatus))
        return this.$t('global.action.uploadProcess')
      return this.$t('trackVideo.connecting')
    },

    bgClass() {
      const isDarkBg =
        this.isFailed || this.isLoading || this.alert.canCheck || this.alert.canRequest
      return {
        'bg-dark-blue': isDarkBg,
        'p-0': this.hasMedia,
      }
    },

    fileExtension() {
      const source = this.alert.videoData?.mediaFilename || this.alert.videoData?.url || ''
      if (this.alert.videoData?.mediaFilename) {
        return this.alert.videoData.mediaFilename.split('.').pop().toLowerCase()
      }
      if (!source) return ''
      try {
        const normalizedUrl = new URL(source, window.location.origin)
        const pathname = normalizedUrl.pathname || ''
        return pathname.split('.').pop().toLowerCase()
      } catch (error) {
        return source.split('?')[0].split('.').pop().toLowerCase()
      }
    },

    isVideoMedia() {
      if (this.alert.videoData?.identifier === 'V') return true

      return VIDEO_EXTENSIONS.has(this.fileExtension)
    },

    isTsFormat() {
      return MUX_REQUIRED_EXTENSIONS.has(this.fileExtension)
    },

    videoSrc() {
      return this.alert.videoData?.url
    },
    videoPlayerOptions() {
      return {
        autoplay: false,
        controls: false,
        muted: true,
        preload: 'auto',
        sources: [{ src: this.videoSrc, type: 'video/mp4' }],
      }
    },
    imageSrc() {
      return this.alert.videoData?.url
    },
    hasMedia() {
      return hasMediaPanelContent(this.alert)
    },
  },
  watch: {
    videoSrc() {
      this.muxFailed = false
      this.isRefreshing = false
      this.refreshedMediaUrl = null
    },
  },

  methods: {
    emitOnce(eventName) {
      if (this.pendingEvents[eventName]) return

      this.$set(this.pendingEvents, eventName, true)
      this.$emit(eventName)

      setTimeout(() => {
        this.$delete(this.pendingEvents, eventName)
      }, 800)
    },

    onMuxReady() {
      this.muxFailed = false
    },

    onMuxError() {
      this.muxFailed = true
      this.onMediaError()
    },

    onMediaError() {
      if (this.isRefreshing || this.refreshedMediaUrl === this.videoSrc) return

      this.isRefreshing = true
      this.refreshedMediaUrl = this.videoSrc
      this.$emit('refresh-media')
    },
  },
}
</script>

<style scoped>
.bg-dark-blue {
  background-color: #0d1b2a !important;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  background-color: #1c74da;
  animation: colorChange 0.4s infinite alternate;
}
.dot:nth-child(2) {
  animation-delay: 0.15s;
}
.dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes colorChange {
  from {
    background-color: #1c74da;
  }
  to {
    background-color: #77abe9;
  }
}

.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
}

.media-wrapper :deep(.video-mux-wrapper) {
  width: 100%;
  height: 100%;
}

.media-wrapper :deep(.mux-video-js) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}
.media-wrapper :deep(.video-js) {
  width: 100%;
  height: 100%;
  background-color: #0d1b2a !important;
  border-radius: 4px;
}

.media-wrapper :deep(.vjs-tech) {
  object-fit: cover;
  border-radius: 4px;
}
</style>
